import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { orderHistoryStyle } from 'components/discovery/pdp/order-history/order-history.style.js'
import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing, type PropertyValues } from 'lit'
import { consume } from '@lit/context'
import { t, TranslateResult } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { validate } from 'typia'
import { validationError } from 'components/discovery/shared/validation.js'
import { HeadingSlotMixin } from 'components/shared/heading/heading-slot-mixin.js'
import { DEFAULT_ROLE_PERMISSIONS, RolePermissionsContext, type RolePermissions } from 'components/shared/role/role-provider.js'
import { orderHistoryApi, GetOrderHistoryRequest } from 'components/discovery/pdp/order-history/api/order-history-api.js'
import { orderHistoryPath } from 'components/discovery/pdp/shared/pdp-endpoint-paths.js'
import { tokenPath } from 'components/discovery/shared/endpoint-paths.js'
import { OrderHistoryItem, type OrderHistory } from './typing/order-history-types.js'

type OrderHistoryStatus = 'idle' | 'loading' | 'success' | 'error'

const translations: { [key: string]: () => TranslateResult } = {
  date: () => t('header_date', {
    ns: 'csc-order-history',
    default: 'Date',
  }),
  quantity: () => t('header_quantity', {
    ns: 'csc-order-history',
    default: 'Quantity',
  }),
  sale_reference: () => t('header_sale_reference', {
    ns: 'csc-order-history',
    default: 'Order number',
  }),
}

@customElement('csc-order-history')
export class CSCOrderHistory extends HeadingSlotMixin(LitElement) {
  static override styles: CSSResultGroup = [hostStyle, orderHistoryStyle]

  @property({ type: Number, attribute: 'product-id' })
  productId: number | null = null

  @consume({ context: RolePermissionsContext, subscribe: true })
  @state()
  private rolePermissions: RolePermissions = DEFAULT_ROLE_PERMISSIONS

  @state()
  private orders: OrderHistoryItem[] = []

  @state()
  private status: OrderHistoryStatus = 'idle'

  private orderHistoryFetched: Promise<void> = Promise.resolve()

  private previousProductId: number | null = null
  private previousLoggedIn = false

  private get isLoggedIn(): boolean {
    return this.rolePermissions.loggedIn
  }

  override async getUpdateComplete(): Promise<boolean> {
    await super.getUpdateComplete()
    await this.orderHistoryFetched
    return super.getUpdateComplete()
  }

  override connectedCallback() {
    super.connectedCallback()

    this.previousProductId = this.productId
    this.previousLoggedIn = this.isLoggedIn

    if (this.isLoggedIn && this.productId !== null) {
      this.orderHistoryFetched = this.fetchOwnOrderHistory(this.productId)
    }
  }

  override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties)

    if (!['productId', 'rolePermissions'].some(property => changedProperties.has(property))) {
      return
    }

    if (this.previousProductId === this.productId && this.previousLoggedIn === this.isLoggedIn) {
      return
    }

    this.previousProductId = this.productId
    this.previousLoggedIn = this.isLoggedIn

    if (this.isLoggedIn && this.productId !== null) {
      this.orderHistoryFetched = this.fetchOwnOrderHistory(this.productId)
    } else {
      this.orders = []
      this.status = 'idle'
    }
  }

  private async fetchOwnOrderHistory(productId: number) {
    this.status = 'loading'

    const [tokenEndpoint, historyEndpoint] = await Promise.all([
      createUrl(tokenPath()),
      createUrl(orderHistoryPath(productId)),
    ])

    if (!this.isConnected || productId !== this.productId) return

    try {
      const response = await orderHistoryApi.getOrderHistory(new GetOrderHistoryRequest(tokenEndpoint.href, historyEndpoint.href))

      if (!this.isConnected || productId !== this.productId) return

      const orderHistoryValidation = validate<OrderHistory>(response)
      if (!orderHistoryValidation.success) {
        validationError('OrderHistory', orderHistoryValidation.errors)
        this.orders = []
        this.status = 'error'
        return
      }

      this.orders = orderHistoryValidation.data
      this.status = 'success'
    } catch {
      if (!this.isConnected || productId !== this.productId) return
      this.orders = []
      this.status = 'error'
    }
  }

  private _dateRenderer = (date: string) => {
    const parsed = new Date(date)
    if (isNaN(parsed.getTime())) {
      return html`${date}`
    }
    return html`
      <cse-text>
        ${new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(parsed)}
      </cse-text>
    `
  }

  private _quantityRenderer = (quantity: string) => {
    return html`
      <cse-text>${quantity}</cse-text>
    `
  }

  private _referenceRenderer = (reference: string, row: OrderHistoryItem) => {
    return html`
      <cse-link url="${row.sale_detail}">${reference}</cse-link>
    `
  }

  private _columns = [
    { name: translations['date']?.(), key: 'sale_date', width: 'auto', cellRenderer: this._dateRenderer },
    { name: translations['quantity']?.(), key: 'quantity', width: 'auto', cellRenderer: this._quantityRenderer },
    { name: translations['sale_reference']?.(), key: 'sale_reference', width: 'auto', cellRenderer: this._referenceRenderer },
  ]

  private tableRenderer = () => {
    if (this.orders.length === 0) {
      return html`
        <cse-text>${t('empty', { ns: 'csc-order-history', default: 'You haven’t placed any orders for this product yet.' })}</cse-text>
      `
    }

    return html`
      <div class="table">
        <csc-table .columns="${this._columns}" .data="${this.orders}" variant="line"></csc-table>
      </div>
    `
  }

  private renderSkeleton = () => html`
    <cse-skeleton height="64" width="350" index="0" variant="secondary"></cse-skeleton>
    <cse-skeleton height="64" width="350" index="1" variant="secondary"></cse-skeleton>
    <cse-skeleton height="64" width="350" index="2" variant="secondary"></cse-skeleton>
  `

  private renderError = () => html`
    <cse-alert variant="error">
      ${t('error', { ns: 'csc-order-history', default: 'Something went wrong while loading your order history. Please try again later.' })}
    </cse-alert>
  `

  private renderContent = () => {
    if (this.status === 'idle' || this.status === 'loading') {
      return this.renderSkeleton()
    }

    if (this.status === 'error') {
      return this.renderError()
    }

    return this.tableRenderer()
  }

  override render = () => {
    if (this.productId === null || !this.isLoggedIn) return nothing

    return html`
      ${this.renderHeadingSlot()}
      ${this.renderContent()}
    `
  }
}
